import { View, html } from 'rune-ts';
import { pipe, range, filter, map, toArray, each } from '@fxts/core';
import { type Users as TUsers } from '../../../../entities/pool/public/users';
import klass from './Users.module.scss';
import Swiper from 'swiper';
import { Autoplay, EffectFade } from 'swiper/modules';
import { classes } from '../../../../shared/util';

export interface UsersProps {
  limit?: number;
  size?: 'small' | 'medium';
  gap?: number;
  users: Required<Pick<TUsers, 'profile_image'>>[];
}

const DEFAULT_THUMBNAILS = [
  '//s3.marpple.co/files/u_4078146/2025/4/original/4eacfeb28995e56af2f252842409246c759bf0ed1.png',
  '//s3.marpple.co/files/u_4078146/2025/4/original/2c1ed6786dcadfbc95ec775eb4d1b459d1fa4e972.png',
  '//s3.marpple.co/files/u_4078146/2025/4/original/7afddc17843b85ee446bd61040abfc7c7e6708b33.png',
] as const;

export interface UserProps extends UsersProps {
  index: number;
  active?: boolean;
  zIndex?: number;
  thumbnail: string;
}

export class User extends View<UserProps> {
  protected override template() {
    const { thumbnail, zIndex = 0 } = this.data;

    return html`
      <div
        class="${classes(klass.users_slide, 'swiper-slide', { [klass.users_active]: this.data.active })}"
        style="z-index: ${zIndex};"
      >
        <img class="${classes(klass.users_thumbnail)}" src="${thumbnail}" alt="" />
      </div>
    `;
  }

  toggle(active: boolean, zIndex = 0) {
    const { limit = 3 } = this.data;
    const is_fade_out = this.element().classList.contains(klass.users_active) && !active;

    if (is_fade_out) this.element().style.zIndex = String(limit + 1);
    else this.element().style.zIndex = String(zIndex);
    this.element().classList.toggle(klass.users_active, active);
  }
}

export class Users extends View<UsersProps> {
  private user_list: User[] = [];
  private position = 0;
  private swiper?: Swiper;

  constructor(props: UsersProps) {
    super(props);
    this.create();
  }

  get defaultThumbnail() {
    const thumbnail = DEFAULT_THUMBNAILS[this.position % DEFAULT_THUMBNAILS.length] || DEFAULT_THUMBNAILS[0];
    this.position++;
    return thumbnail;
  }

  protected override template() {
    const size = this.getSize();

    return this.user_list.length > 0
      ? html`
          <div
            class="${klass.users} ${size} swiper swiper-initialized swiper-horizontal swiper-backface-hidden"
          >
            <div class="${klass.users_wrapper} ${size} swiper-wrapper">${this.user_list}</div>
          </div>
        `
      : html`<div></div>`;
  }

  protected override onRender() {
    this.initSwiper();
  }

  private initSwiper() {
    const { gap = -8.5, limit = 3 } = this.data;
    this.swiper = new Swiper(`.${klass.users}`, {
      modules: [Autoplay, EffectFade],
      slidesPerView: this.user_list.length >= 3 ? 3 : this.user_list.length,
      spaceBetween: gap,
      loop: true,
      autoplay: {
        delay: 2500,
        disableOnInteraction: false,
      },
      allowTouchMove: false,
      simulateTouch: false,
    });

    this.swiper.on('slideChangeTransitionStart', (s) => {
      requestAnimationFrame(() => {
        const active_indexes = this.getActiveUserIndexes(s.realIndex);
        pipe(
          this.user_list,
          each((user) => {
            user.toggle(
              active_indexes.includes(user.data.index),
              active_indexes.includes(user.data.index) ? limit - active_indexes.indexOf(user.data.index) : 0,
            );
          }),
        );
      });
    });
  }

  getActiveUserIndexes(realIndex = 0, limit = 3) {
    return pipe(
      range(this.data.users.length),
      filter((i) => i < limit),
      map((i) => (realIndex + i) % this.data.users.length),
      toArray,
    );
  }

  getSize() {
    const length = this.data.users.length;

    if (length === 1) return klass.smal;
    if (length === 2) return klass.middle;
    return klass.large;
  }

  create(users: Required<Pick<TUsers, 'profile_image'>>[] = []) {
    const { limit = 3 } = this.data;
    this.position = 0;
    this.data.users = [...users, ...this.data.users];

    const active_indexes = this.getActiveUserIndexes();
    this.user_list =
      this.data.users?.map(
        (_, index) =>
          new User({
            ...this.data,
            index,
            active: active_indexes.includes(index),
            zIndex: active_indexes.includes(index) ? limit - active_indexes.indexOf(index) : 0,
            thumbnail: this.data.users[index]?.profile_image || this.defaultThumbnail,
          }),
      ) || [];
  }
}
